import React, { useState, useEffect } from 'react';
import { useAuth } from '@/components/auth/AuthContext';
import AdminLayout from '@/components/layouts/AdminLayout';
import { toast } from 'sonner';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { 
  Pagination, 
  PaginationContent, 
  PaginationItem, 
  PaginationLink, 
  PaginationNext, 
  PaginationPrevious 
} from '@/components/ui/pagination';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose,
} from "@/components/ui/dialog";
import { 
  PlusCircle, 
  Pencil, 
  Trash2, 
  Eye, 
  Search, 
  Loader2, 
  FileEdit 
} from 'lucide-react';
import * as blogApi from '@/api/blogApi';
import { Blog, BlogCategory } from '@/types/blog';
import { formatDistanceToNow } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';

const AdminBlogManagement = () => {
  const { user } = useAuth();
  const [blogs, setBlogs] = useState<Blog[]>([]);
  const [totalBlogs, setTotalBlogs] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedBlog, setSelectedBlog] = useState<Blog | null>(null);
  const [deletingBlog, setDeletingBlog] = useState<Blog | null>(null);
  const [blogStatus, setBlogStatus] = useState<'all' | 'draft' | 'published'>('all');
  const [userRole, setUserRole] = useState<{ is_admin?: boolean, is_content_editor?: boolean }>({});
  const pageSize = 10;

  // Check user permissions on mount
  useEffect(() => {
    const checkUserPermissions = async () => {
      if (user) {
        try {
          // Get user's profile which contains role information
          const { data, error } = await supabase
            .from('profiles')
            .select('is_admin, is_content_editor')
            .eq('id', user.id)
            .single();
            
          if (error) {
            console.error('Error fetching user profile:', error);
          } else {
            console.log('User permissions:', data);
            setUserRole(data || {});
          }
        } catch (error) {
          console.error('Error checking user permissions:', error);
        }
      }
    };
    
    checkUserPermissions();
  }, [user]);

  useEffect(() => {
    fetchBlogs();
  }, [currentPage, searchTerm, blogStatus]);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      console.log('Fetching blogs with status:', blogStatus);
      
      // Pass undefined for "all" to avoid filtering at the database level
      const status = blogStatus === 'all' ? undefined : blogStatus;
      
      // Ensure session is valid before fetching
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        console.warn('No active session found when fetching blogs');
        toast.error('You must be logged in to view blog posts');
        setLoading(false);
        return;
      } 
      
      console.log('Using authenticated session for blog fetch');
      
      // Fetch blogs from API with descending sort
      const response = await blogApi.getBlogs(currentPage, pageSize, status, undefined, 'desc');
      
      // Handle different response formats
      let blogsList = [];
      let totalCount = 0;
      
      // New Edge Function format
      if (response.data && response.pagination) {
        blogsList = response.data;
        totalCount = response.pagination.totalItems;
        console.log('Using new Edge Function response format');
      } 
      // Old direct Supabase client format
      else if (Array.isArray(response) || (response as any).blogs) {
        // Handle legacy format where the response might be an array or have a blogs property
        blogsList = Array.isArray(response) ? response : (response as any).blogs || [];
        totalCount = Array.isArray(response) ? blogsList.length : (response as any).total || blogsList.length;
        console.log('Using old Supabase client response format');
      }
      // Unknown format
      else {
        console.error('Unexpected API response format:', response);
        blogsList = [];
        totalCount = 0;
      }
      
      console.log(`Found ${blogsList.length} blog posts with status: ${status || 'all'}`);
      
      // Log the breakdown of status for debugging when "All" is selected
      if (blogStatus === 'all' && blogsList.length > 0) {
        const draftCount = blogsList.filter((blog: Blog) => blog.status === 'draft').length;
        const publishedCount = blogsList.filter((blog: Blog) => blog.status === 'published').length;
        console.log(`Status breakdown - Draft: ${draftCount}, Published: ${publishedCount}`);
      }
      
      // Client-side search filtering if needed
      if (searchTerm && blogsList.length > 0) {
        const search = searchTerm.toLowerCase();
        blogsList = blogsList.filter((blog: Blog) => 
          blog.title.toLowerCase().includes(search) || 
          blog.slug.toLowerCase().includes(search) ||
          blog.content.toLowerCase().includes(search)
        );
      }
      
      setBlogs(blogsList);
      setTotalBlogs(totalCount);
    } catch (error) {
      console.error('Error fetching blogs:', error);
      toast.error('Failed to load blogs');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBlog = async () => {
    if (!deletingBlog) return;
    
    try {
      await blogApi.deleteBlog(deletingBlog.id);
      toast.success('Blog post deleted successfully');
      setDeletingBlog(null);
      fetchBlogs();
    } catch (error) {
      console.error('Error deleting blog:', error);
      toast.error('Failed to delete blog post');
    }
  };

  const formatDate = (dateString: string) => {
    return formatDistanceToNow(new Date(dateString), { addSuffix: true });
  };

  const totalPages = Math.ceil(totalBlogs / pageSize);

  return (
    <AdminLayout>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Blog Management</CardTitle>
            <CardDescription>Manage your blog posts</CardDescription>
          </div>
          <Button onClick={() => window.location.href = '/admin/blogs/new'}>
            <PlusCircle className="h-4 w-4 mr-2" />
            New Blog Post
          </Button>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="relative w-64">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search blogs..."
                  className="pl-8"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button 
                variant={blogStatus === 'all' ? 'default' : 'outline'} 
                onClick={() => setBlogStatus('all')}
                size="sm"
              >
                All
              </Button>
              <Button 
                variant={blogStatus === 'published' ? 'default' : 'outline'} 
                onClick={() => setBlogStatus('published')}
                size="sm"
              >
                Published
              </Button>
              <Button 
                variant={blogStatus === 'draft' ? 'default' : 'outline'} 
                onClick={() => setBlogStatus('draft')}
                size="sm"
              >
                Drafts
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center items-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Title</TableHead>
                      <TableHead>Slug</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Last Updated</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {blogs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                          No blog posts found
                        </TableCell>
                      </TableRow>
                    ) : (
                      blogs.map((blog) => (
                        <TableRow key={blog.id}>
                          <TableCell className="font-medium">{blog.title}</TableCell>
                          <TableCell>{blog.slug}</TableCell>
                          <TableCell>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              blog.status === 'published' 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {blog.status}
                            </span>
                          </TableCell>
                          <TableCell>{formatDate(blog.created_at)}</TableCell>
                          <TableCell>{formatDate(blog.updated_at)}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end items-center gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => window.open(`/blog/${blog.slug}`, '_blank')}
                                title="View"
                                className="flex items-center gap-1"
                              >
                                <Eye className="h-4 w-4" />
                                View
                              </Button>
                              <Button
                                variant="default"
                                size="sm"
                                onClick={() => window.location.href = `/admin/blogs/edit/${blog.id}`}
                                title="Edit"
                                className="flex items-center gap-1"
                              >
                                <Pencil className="h-4 w-4" />
                                Edit
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-destructive"
                                onClick={() => setDeletingBlog(blog)}
                                title="Delete"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              {totalPages > 1 && (
                <Pagination className="mt-4">
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious 
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        className={currentPage <= 1 ? "pointer-events-none opacity-50" : ""}
                      />
                    </PaginationItem>
                    
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <PaginationItem key={page}>
                        <PaginationLink
                          onClick={() => setCurrentPage(page)}
                          isActive={currentPage === page}
                        >
                          {page}
                        </PaginationLink>
                      </PaginationItem>
                    ))}
                    
                    <PaginationItem>
                      <PaginationNext 
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        className={currentPage >= totalPages ? "pointer-events-none opacity-50" : ""}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deletingBlog} onOpenChange={(open) => !open && setDeletingBlog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Blog Post</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deletingBlog?.title}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletingBlog(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteBlog}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminBlogManagement; 